import { useSubjectMember } from '@healthblocks-io/core'
import { t } from '@lingui/macro'
import { type FC, type ReactNode, useMemo, useState } from 'react'
import { useSetRecoilState } from 'recoil'
import { useTheme } from 'styled-components'

import { FlexColumn } from '@/@bothrs-ui/web/components/layout'
import { Button } from '@/@bothrs-ui/web/components/primitives/button/Button'
import { Span } from '@/@bothrs-ui/web/components/typography/Body'
import { H1 } from '@/@bothrs-ui/web/components/typography/Heading'
import { mixpanel } from '@/mixpanel'
import { useAuthenticedSession } from '@/modules/auth/hooks/useAuthenticatedSession'
import { useAuthentication } from '@/modules/auth/hooks/useAuthentication'
import { useLogout } from '@/modules/auth/hooks/useLogout'
import { useBiometrics } from '@/modules/biometrics/hooks/useBiometrics'
import { useBiometricsPermission } from '@/modules/biometrics/hooks/useBiometricsPermission'
import { BiometricsAppPermission } from '@/modules/biometrics/state/biometrics.atom'
import { elapsedTimeAtom } from '@/modules/idle-timer/state/idle-timer.atoms'
import { Loader } from '@/modules/shared/components/loader/Loader'
import { useLockoutTime } from '@/modules/shared/hooks/useLockoutTime'
import { useMemberEvent } from '@/modules/shared/hooks/useMemberEvent'
import { isIOS } from '@/utils/environment'

export type Props = {
  /**
   * Component to show a message configured by the Admin in Healthblocks
   */
  message: ReactNode

  /**
   * Callback when user presses the debug button
   * */
  onDebugPress: () => void
}

/**
 * Component to show the login content for eHealth authentication
 * (initial screen when user is not authenticated)
 */
export const AppLockedContent: FC<Props> = ({ message, onDebugPress }) => {
  // State
  // ------------------------------------------------------------------------- /
  const [biometricsFailed, setBiometricsFailed] = useState<boolean>(false)

  // Hooks
  // ------------------------------------------------------------------------- /

  const { isAvailable: biometricsAvailable, verifyIdentity } = useBiometrics()
  const { biometricPermission, updateBiometricsPermission } = useBiometricsPermission()
  const { lockout, setLockout } = useLockoutTime()
  const setLastInteraction = useSetRecoilState(elapsedTimeAtom)
  const { isSessionExpired } = useAuthenticedSession()
  const { authenticate, authenticatedAndLoading, isAuthenticated, shouldBeLoading } = useAuthentication()
  const subjectMember = useSubjectMember()

  const { logout } = useLogout()
  const trackMemberEvent = useMemberEvent()

  const theme = useTheme()

  // Template variables
  // ------------------------------------------------------------------------- /

  /**
   * The user can authenticate with biometrics if:
   *  - Biometrics are available on the device
   *  - The user has not failed biometrics authentication
   *  - The user has been locked out
   *  - The user is authenticated
   *  - The authentication session is not expired
   *   - The user has granted biometrics permission
   *
   */
  const canAuthenticateWithBiometrics: boolean = useMemo(() => {
    if (!biometricsAvailable || isSessionExpired || !lockout || !isAuthenticated) {
      return false
    }

    // We cannot track the permission on iOS, so use the biometricsAvailable flag
    // If the user has setup biometrics in the app (meaning the biometricsPermission is not SKIPPED or PROMPT),
    // we use the biometricsAvailable (because this will tell us whether the user has biometrics available AND granted on the device)
    if (isIOS()) {
      return biometricPermission !== BiometricsAppPermission.SKIPPED &&
        biometricPermission !== BiometricsAppPermission.PROMPT
        ? biometricsAvailable
        : false
    }

    return biometricPermission === BiometricsAppPermission.GRANTED
  }, [biometricsAvailable, biometricPermission, lockout, isSessionExpired, isAuthenticated])

  // Interaction methods
  // ------------------------------------------------------------------------- /
  const handleLogin = async () => {
    if (canAuthenticateWithBiometrics && !biometricsFailed) {
      try {
        const identityVerified = await verifyIdentity()

        mixpanel.track('biometrics_login', {
          status: identityVerified ? 'success' : 'failed',
        })

        if (identityVerified) {
          // Track successful biometric login to API
          trackMemberEvent('biometric')

          setLockout(false)
          // Reset idle timer to avoid immediate re-lockout from stale timestamp
          setLastInteraction(Date.now())
          setBiometricsFailed(false)
        } else {
          setBiometricsFailed(true)
          setLockout(true)
          updateBiometricsPermission(BiometricsAppPermission.PROMPT)
        }
      } catch {
        setBiometricsFailed(true)
        updateBiometricsPermission(BiometricsAppPermission.PROMPT)
      }

      return
    }

    if (biometricsFailed) {
      mixpanel.track('lock_screen_login_after_biometrics_failed')
    } else {
      mixpanel.track('login_with_ehealth_to_unlock')
    }

    await authenticate()
  }

  const handleLogout = async () => {
    if (biometricsFailed) {
      mixpanel.track('lock_screen_logout_after_biometrics_failed')
    } else if (canAuthenticateWithBiometrics) {
      mixpanel.track('lock_screen_login_with_other_user')
    } else {
      mixpanel.track('lock_screen_logout')
    }
    await logout()
  }

  // Render
  // ------------------------------------------------------------------------- /
  if (shouldBeLoading || authenticatedAndLoading) {
    return <Loader cancellable />
  }

  return (
    <>
      {/* Title */}
      <H1 noMargin onClick={onDebugPress}>
        {biometricsFailed
          ? t({
              id: 'login.biometrics-failed',
              message: 'De identificatie is niet gelukt.',
            })
          : canAuthenticateWithBiometrics && subjectMember?.givenName
            ? t({
                id: 'login.welcome-back',
                message: 'Dag {name}',
                values: { name: subjectMember.givenName },
              })
            : t({
                id: 'login.welcome',
              })}
      </H1>

      {/* Show message configured in Healthblocks by Admin */}
      {message}

      {/* Message Body */}
      <Span type='BodyLargeRegular'>
        {biometricsFailed
          ? t({
              id: 'login.description.biometrics-failed',
              message: 'Meld je opnieuw aan met eHealth',
            })
          : t({
              id: 'login.description.lockout',
              message: 'Om veiligheidsredenen hebben we je afgemeld. Meld je hieronder terug aan.',
            })}
      </Span>

      {/* Buttons */}
      <FlexColumn gap={theme.UI.Spacing.Space3}>
        <Button
          variant='Primary'
          isLoading={authenticatedAndLoading}
          disabled={authenticatedAndLoading}
          onClick={handleLogin}
        >
          {canAuthenticateWithBiometrics && !biometricsFailed
            ? t({
                id: 'login.action.biometrics',
                message: 'Aanmelden',
              })
            : t({
                id: 'login.action.lockout',
                message: 'Meld je opnieuw aan',
              })}
        </Button>

        <Button
          variant='Secondary'
          isLoading={authenticatedAndLoading}
          disabled={authenticatedAndLoading}
          onClick={handleLogout}
        >
          {biometricsFailed
            ? t({
                id: 'login.action.logout',
                message: 'Afmelden',
              })
            : t({
                id: 'login.action.login-other',
                message: 'Aanmelden als andere gebruiker',
              })}
        </Button>
      </FlexColumn>
    </>
  )
}
